import type { ReactNode } from 'react'
import type {
  DataTableCell,
  DataTableCellValue,
  DataTableInstance,
  DataTableRowData,
} from '../../types'

interface Props<TData extends DataTableRowData, TValue = DataTableCellValue> {
  cell: DataTableCell<TData, TValue>
  renderedColumnIndex?: number
  renderedRowIndex?: number
  table: DataTableInstance<TData>
}

export const TableBodyCellValue = <TData extends DataTableRowData>({
  cell,
  renderedColumnIndex = 0,
  renderedRowIndex = 0,
  table,
}: Props<TData>) => {
  const { column, row } = cell
  const { columnDef } = column

  let renderedCellValue: ReactNode

  if (cell.getIsAggregated() && columnDef.AggregatedCell) {
    renderedCellValue = columnDef.AggregatedCell({
      cell,
      column,
      row,
      table,
    })
  } else if (row.getIsGrouped() && !cell.getIsGrouped()) {
    renderedCellValue = null
  } else if (cell.getIsGrouped() && columnDef.GroupedCell) {
    renderedCellValue = columnDef.GroupedCell({
      cell,
      column,
      row,
      table,
    })
  } else {
    renderedCellValue = undefined
  }

  const isGroupedValue = renderedCellValue !== undefined

  if (!isGroupedValue) {
    renderedCellValue = cell.renderValue() as number | string
  }

  if (columnDef.Cell && !isGroupedValue) {
    renderedCellValue = columnDef.Cell({
      cell,
      column,
      renderedCellValue,
      renderedColumnIndex,
      renderedRowIndex,
      row,
      table,
    })
  }

  return renderedCellValue
}
